import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useTheme } from '@shared/providers/theme/ThemeContext';
import useConsentTreatmentPeriodList from '@/hooks/main/useConsentTreatmentPeriodList';
import useConsentTreatmentPeriodPrint from '@/hooks/main/useConsentTreatmentPeriodPrint';
import useAppToast from '@/hooks/useAppToast';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import ConsentTreatmentPeriodSearchForm from '@/pages/main/consent/components/ConsentTreatmentPeriodSearchForm';
import ConsentTreatmentPeriodTable, {
  type SelectionState,
} from '@/pages/main/consent/components/ConsentTreatmentPeriodTable';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { DEFAULT_PER_PAGE } from '@/constants';
import { TENANT_ROUTES } from '@/constants/routes';
import type {
  ConsentTreatmentPeriodListParams,
  ConsentTreatmentPeriodPair,
  ConsentTreatmentPeriodPrintPayload,
  ConsentTreatmentPeriodSortKey,
  SortDir,
} from '@/types/consentTreatmentPeriod.types';
import type { ConsentTreatmentPeriodSearchFormData } from '@/schemas/consentTreatmentPeriodSchema';

const initialSelection = (): SelectionState => ({ mode: 'manual', selected: new Set() });

const parseKey = (key: string): ConsentTreatmentPeriodPair | null => {
  const [cid, mid] = key.split('@');
  const consent_id = Number(cid);
  if (!Number.isFinite(consent_id)) return null;
  // "null" sentinel = consent-only row (no visit/massager in treatment term).
  if (mid === 'null' || mid === undefined) {
    return { consent_id, massager_id: null };
  }
  const massager_id = Number(mid);
  return { consent_id, massager_id: Number.isFinite(massager_id) ? massager_id : null };
};

export const ConsentTreatmentPeriodListPage: React.FC = () => {
  const { tenant } = useParams();
  const { serviceType: contextServiceType } = useTheme();
  const isA2 = (contextServiceType ?? '').toUpperCase() === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const toast = useAppToast();
  const [filters, setFilters] = useState<ConsentTreatmentPeriodListParams | undefined>(undefined);
  const [outputOption, setOutputOption] = useState(true);
  const [sortField, setSortField] = useState<ConsentTreatmentPeriodSortKey | null>(null);
  const [sortOrder, setSortOrder] = useState<SortDir | null>(null);
  const [selection, setSelection] = useState<SelectionState>(initialSelection());

  const queryEnabled = !!filters;
  const { data, isFetching } = useConsentTreatmentPeriodList(filters, { enabled: queryEnabled });
  const listBody = data?.data;
  const rows = listBody?.rows ?? [];
  const pagination = listBody?.pagination ?? null;

  const { listPdf, labelPdf, csv } = useConsentTreatmentPeriodPrint();

  useEffect(() => {
    setSelection(initialSelection());
  }, [contextServiceType]);

  const handleSearch = (values: ConsentTreatmentPeriodSearchFormData) => {
    // BD: 検索 resets sort to default AND clears any prior selection.
    setSortField(null);
    setSortOrder(null);
    setSelection(initialSelection());
    setFilters({
      search_treatement_from: values.search_treatement_from,
      search_treatement_to: values.search_treatement_to,
      massager: values.massager,
      massager_id: values.massager_id ?? undefined,
      page: 1,
      per_page: DEFAULT_PER_PAGE,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => (prev ? { ...prev, page } : prev));
  };

  const handleSortChange = (field: ConsentTreatmentPeriodSortKey | null, order: SortDir | null) => {
    // 3-state toggle is done in Table onChange; page just stores the result and
    // forwards to BE via sort_by/sort_dir (or clears both when null).
    setSortField(field);
    setSortOrder(order);
    setFilters(prev =>
      prev
        ? {
            ...prev,
            page: 1,
            sort_by: field ?? undefined,
            sort_dir: order ?? undefined,
          }
        : prev
    );
  };

  const buildEditConsentHref = (patientId: number, consentId: number) =>
    TENANT_ROUTES.CONSENTS.EDIT(tenant ?? '', patientId, consentId);

  const basePrintPayload = useMemo(
    (): Partial<ConsentTreatmentPeriodPrintPayload> => ({
      search_treatement_from: filters?.search_treatement_from,
      search_treatement_to: filters?.search_treatement_to,
      // Forward massager mode so BE re-query in select-all mode matches the list
      // exactly (BE only applies massager_id when mode='2').
      massager: filters?.massager,
      massager_id: filters?.massager_id,
    }),
    [filters]
  );

  const buildPrintPayload = (): ConsentTreatmentPeriodPrintPayload => {
    if (selection.mode === 'all') {
      const excluded_pairs = Array.from(selection.excluded)
        .map(parseKey)
        .filter((p): p is ConsentTreatmentPeriodPair => p !== null);
      return { ...basePrintPayload, select_all: true, excluded_pairs };
    }
    const pairs = Array.from(selection.selected)
      .map(parseKey)
      .filter((p): p is ConsentTreatmentPeriodPair => p !== null);
    return { ...basePrintPayload, pairs };
  };

  const onListPdf = () => {
    listPdf.mutate({ ...buildPrintPayload(), output_option: outputOption });
  };
  const onLabelPdf = () => {
    labelPdf.mutate(buildPrintPayload());
  };
  const onCsv = () => {
    csv.mutate(buildPrintPayload());
  };

  return (
    <div className="pb-4">
      <CollapsibleCard title="加療期間期限切れリスト">
        <ConsentTreatmentPeriodSearchForm
          massagerLabel={massagerLabel}
          onSearch={handleSearch}
          onValidationError={msg => toast.error(msg)}
          isLoading={isFetching}
        />
      </CollapsibleCard>

      {queryEnabled && (
        <CollapsibleCard>
          <ConsentTreatmentPeriodTable
            rows={rows}
            pagination={pagination}
            massagerLabel={massagerLabel}
            buildEditConsentHref={buildEditConsentHref}
            onPageChange={handlePageChange}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            selection={selection}
            onSelectionChange={setSelection}
            isLoading={isFetching}
            outputOption={outputOption}
            onOutputOptionChange={setOutputOption}
            onListPdf={onListPdf}
            onLabelPdf={onLabelPdf}
            onCsv={onCsv}
            hasSearched={queryEnabled}
          />
        </CollapsibleCard>
      )}
    </div>
  );
};

export default ConsentTreatmentPeriodListPage;
